import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useForm } from "react-hook-form";
import produce from "immer";

import "./UpdateTypes.scss";
import { ErrorBoundary, ErrorMessage, FormInput, Loader, FormSelect, Page, PageHeader } from "components";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, IconButton} from '@cfa/react-components';
import { AsyncStatus } from "enums";
import { IUpdateType, IUpdateTypeAPI } from "interfaces";
import { ManufacturersService, UpdateTypesService, parseAxiosError } from "services";

interface IUpdateTypePageState {
    loadStatus: AsyncStatus;
    loadError?: string;
    deleteStatus?: AsyncStatus;
    deleteError?: string;
    createStatus?: AsyncStatus;
    createError?: string;
    updateTypes?: IUpdateType[];
    deleteUpdateType?: IUpdateType;
}

type IUpdateTypeForm = {
    manufacturerId: number;
    updateType: string;
    description: string;
    topicTypeId: number;
};

export function UpdateTypes() {

    const [pageState, setPageState] = useState<IUpdateTypePageState>({
        loadStatus: AsyncStatus.Loading,
        createStatus: AsyncStatus.Initial,
        deleteStatus: AsyncStatus.Initial,
        updateTypes: [],
    });
    const { loadStatus, loadError, deleteStatus, deleteError, createStatus, createError, updateTypes, deleteUpdateType } = pageState;
    const form = useForm<IUpdateTypeForm>({ mode: "onTouched" });
    const topicTypes = [{name: "Firmware", id: 1}, {name: "Menu", id: 2}];

    // create modal control
    const [createShow, setCreateShow] = useState(false);
    const handleCreateClose = () => setCreateShow(false);
    const showCreateModal = () => {
        setCreateShow(true);
    };

    // delete modal control
    const [deleteShow, setDeleteShow] = useState(false);
    const handleDeleteClose = () => setDeleteShow(false);
    const showDeleteModal = (uType) => {
        setPageState(
            produce((draft) => {
                draft.deleteUpdateType = uType;
            })
        );
        setDeleteShow(true);
    };

    // data is preloaded in app.tsx, so just assign from service
    useEffect(() => {
        if (loadStatus === AsyncStatus.Success) return;

        setPageState(
            produce((draft) => {
                draft.loadStatus = AsyncStatus.Success;
                draft.updateTypes = UpdateTypesService.updateTypes;
            })
        );
    });
 
    // Soft deletes the UpdateType in the DB
    function onDeleteUpdateType(uType: IUpdateType) {
        if (deleteStatus === AsyncStatus.Loading) return;

        setPageState(
            produce((draft) => {
                draft.deleteUpdateType = uType;
                draft.deleteError = '';
                draft.deleteStatus = AsyncStatus.Loading;
            })
        )

        UpdateTypesService.deleteUpdateType(uType.id)
            .then(() => {
                setPageState(
                    produce((draft) => {
                        draft.deleteUpdateType = undefined;
                        draft.deleteStatus = AsyncStatus.Success;
                        draft.updateTypes = UpdateTypesService.updateTypes;
                    })
                )
                setDeleteShow(false);
            })
            .catch((err) => {
                setPageState(
                    produce((draft) => {
                        draft.deleteError = parseAxiosError(err);
                        draft.deleteStatus = AsyncStatus.Error;
                    })
                );
            });
        
    }

    const onCreateUpdateType = () => {
        if (createStatus === AsyncStatus.Loading) return;

        form.handleSubmit((formData: IUpdateTypeForm) => {
            if (!formData.manufacturerId || !formData.topicTypeId) return;

            // Setstate to loading
            setPageState(produce(draft => {
                draft.createError = '';
                draft.createStatus = AsyncStatus.Loading;
            }));

            let payload: IUpdateTypeAPI = {
                manufacturerId: Number(formData.manufacturerId),
                updateType: formData.updateType,
                description: formData.description,
                topicTypeId: Number(formData.topicTypeId),
            }
            
            UpdateTypesService.postUpdateType(payload)
                .then(() => {
                    setPageState(produce(draft => {
                        draft.createError = '';
                        draft.createStatus = AsyncStatus.Success;
                        draft.updateTypes = UpdateTypesService.updateTypes;
                    }));
                    setCreateShow(false);
                })
                .catch((err) => {
                    setPageState(
                        produce((draft) => {
                            draft.createError = parseAxiosError(err);
                            draft.createStatus = AsyncStatus.Error;
                        })
                    );
                });
        })();
    };    

    function onChangeManufacturer(e) {
        form.setValue('updateType', '');
        form.setValue('description', '');
        form.setValue('topicTypeId', undefined);
    }

    return (<>
        <Page>
            <PageHeader title="Update Types" right={<Button onClick={showCreateModal}>{'New Update Type'}</Button>} />

            <div className="row updatetypes_colHeaders">
                <div className="updatetypes_colHeader updatetypes_colName">Update Type</div>
                <div className="updatetypes_colHeader updatetypes_colNameHeader">Manufacturer</div>
                <div className="updatetypes_colHeader updatetypes_colDescriptionHeader">Description</div>
            </div>
            
            <div className="updatetypes_list">
                <ErrorBoundary>
                    {loadStatus === AsyncStatus.Loading && <Loader />}
                    {loadStatus === AsyncStatus.Success &&
                        updateTypes.length > 0 && updateTypes.map((uType) => {
                            return (
                                <div className="updatetypes_listItem" key={uType.id}>
                                    <div className="updatetypes_colName">{uType.updateType}</div>
                                    <div className="updatetypes_col">{
                                        ManufacturersService.manufacturersMap[uType.manufacturerId]?.name || "Not Found"
                                    }</div>
                                    <div className="updatetypes_colDescription">{uType.description}</div>
                                    <div>
                                        <IconButton
                                            size="sm"
                                            disabled={uType.isDeleted}
                                            onClick={() => showDeleteModal(uType)}
                                        >
                                            <FontAwesomeIcon icon="trash" />
                                        </IconButton>
                                    </div>
                                </div>
                            );
                        })}
                    {loadStatus === AsyncStatus.Success &&
                        updateTypes.length === 0 && (
                            <div className="updatetypes_listEmpty">
                                <FontAwesomeIcon
                                    icon="search"
                                    className="updatetypes_listEmptyIcon"
                                />
                                <div className="updatetypes_listEmptyText">
                                    No update types found.
                                </div>
                            </div>
                        )}
                    {loadStatus === AsyncStatus.Error && (
                        <ErrorMessage message={loadError} />
                    )}
                </ErrorBoundary>
            </div>
        </Page>

        <Modal show={deleteShow} onClose={handleDeleteClose}>
            <ModalHeader>
                Delete Update Type
            </ModalHeader>
            <ModalBody>
                <p>Are you sure you want to delete this update type?</p>
                <div className="updatetypes_deleteHeader">
                    Update Type: {deleteUpdateType && deleteUpdateType.updateType}<br/>
                    ID: {deleteUpdateType && deleteUpdateType.id}
                </div>
            </ModalBody>
            <ModalFooter>
                {deleteStatus === AsyncStatus.Error && (
                    <ErrorMessage message={deleteError} />
                )}
                <Button variant="outlined" disabled={deleteStatus === AsyncStatus.Loading} onClick={handleDeleteClose}>
                    Cancel
                </Button>
                <Button loading={deleteStatus === AsyncStatus.Loading} onClick={() => onDeleteUpdateType(deleteUpdateType)}>
                    Delete
                </Button>
            </ModalFooter>
        </Modal>

        <Modal show={createShow} onClose={handleCreateClose}>
            <ModalHeader>
                Create Update Type
            </ModalHeader>
            <ModalBody>
                <div>
                    {createStatus === AsyncStatus.Error && (
                        <ErrorMessage message={createError} />
                    )}
                    <div className="row">
                        <div className="col">
                            <FormSelect
                                form={form}
                                getOptionLabel={m => m.name}
                                getOptionValue={m => m.id}
                                label="Manufacturer"
                                name="manufacturerId"
                                options={ManufacturersService.manufacturers.filter(m => (!m.isDeleted))}
                                onChange={onChangeManufacturer}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <FormSelect
                                form={form}
                                getOptionLabel={t => t.name}
                                getOptionValue={t => t.id}
                                label="Topic Type"
                                name="topicTypeId"
                                options={topicTypes}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <FormInput
                                form={form}
                                label="Update Type"
                                name="updateType"
                                registerOptions={{ required: true }}
                            />   
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <FormInput
                                form={form}
                                label="Description"
                                name="description"
                                registerOptions={{ required: true }}
                            />   
                        </div>
                    </div>      
                </div>
            </ModalBody>
            <ModalFooter>
                <Button variant="outlined" disabled={createStatus === AsyncStatus.Loading} onClick={handleCreateClose}>
                    Cancel
                </Button>
                <Button loading={createStatus === AsyncStatus.Loading} disabled={!form.formState.isValid} onClick={onCreateUpdateType}>
                    Submit
                </Button>
            </ModalFooter>
        </Modal>
    </>);
}
